import { Link } from 'react-router-dom'
import { useAuth } from '@/state/AuthContext.tsx'
import { SECTIONS } from '@/routes/registry.ts'
import { LeafDivider, Bloom } from '@/components/Botanical.tsx'
import { useNames, usePicker } from '@/features/names/useNames.ts'
import { useNews } from '@/features/news/useNews.ts'

function greeting(): string {
  const h = new Date().getHours()
  if (h < 5) return 'Still up'
  if (h < 12) return 'Good morning'
  if (h < 18) return 'Good afternoon'
  return 'Good evening'
}

/** A single name, offered without ceremony. Different every time the page loads. */
function NameOfTheMoment() {
  const { all } = useNames()
  const { current } = usePicker(all, 'any')
  if (!current) return null

  return (
    <Link to="/names" className="group block">
      <p className="eyebrow">A name, at random</p>
      <p className="mt-3 font-display text-[2.4rem] leading-none transition-colors duration-300 group-hover:text-fern">
        {current.name}
      </p>
      {current.meaning && (
        <p className="prose-soft mt-3 text-[0.92rem] italic">
          <span className="not-italic text-whisper">{current.origin}</span> — {current.meaning}
        </p>
      )}
    </Link>
  )
}

/** The single most encouraging thing we found this morning. */
function OneGoodThing() {
  const { stories } = useNews()
  const story = stories[0]
  if (!story) return null

  return (
    <a href={story.link} target="_blank" rel="noopener noreferrer" className="group block">
      <p className="eyebrow">One good thing</p>
      <p className="mt-3 font-display text-[1.5rem] leading-[1.26] transition-colors duration-300 group-hover:text-fern">
        {story.title}
      </p>
      <p className="mt-3 text-[0.78rem] uppercase tracking-[0.16em] text-sage">
        {story.source}
        <span className="inline-block transition-transform duration-300 ease-botanical group-hover:translate-x-1"> →</span>
      </p>
    </a>
  )
}

export default function Home() {
  const { user } = useAuth()
  const firstName = user?.displayName.split(' ')[0] ?? ''

  return (
    <>
      <section className="relative max-w-measure animate-unfurl pb-4 pt-10 sm:pt-16">
        <Bloom tone="var(--c-clay)" className="pointer-events-none absolute -right-4 -top-2 h-12 w-12 opacity-40 animate-drift sm:-right-16" aria-hidden />
        <p className="eyebrow">
          {greeting()}, {firstName}
        </p>
        <h1
          className="mt-5 font-display text-[2.9rem] leading-[1.04] sm:text-[3.9rem]"
          style={{ fontVariationSettings: "'SOFT' 68, 'WONK' 1" }}
        >
          You are allowed to take as long as you need.
        </h1>
        <p className="prose-soft mt-7 text-[1.06rem]">
          This is somewhere to put it all down — the names, the good news, the questions neither of
          you has said out loud yet. Nothing here is a decision. It is just a place to think.
        </p>
      </section>

      <LeafDivider tone="var(--c-sage)" className="my-16 h-5 w-full opacity-30" />

      <section className="grid gap-14 sm:grid-cols-2 sm:gap-16">
        <NameOfTheMoment />
        <OneGoodThing />
      </section>

      <LeafDivider tone="var(--c-clay)" className="my-16 h-5 w-full opacity-25" />

      <nav>
        <p className="eyebrow">Where to go</p>
        <ul className="mt-6 divide-y divide-linen border-y border-linen">
          {SECTIONS.map((s, i) => (
            <li key={s.path}>
              <Link to={s.path} className="group flex items-center gap-6 py-7">
                <span className="w-8 shrink-0 font-display text-[0.9rem] text-whisper transition-colors group-hover:text-sage">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-display text-[1.6rem] leading-tight transition-colors duration-300 group-hover:text-fern">
                    {s.label}
                  </span>
                  <span className="prose-soft mt-1.5 block text-[0.92rem]">{s.blurb}</span>
                </span>
                <span className="shrink-0 text-sage opacity-0 transition-all duration-300 ease-botanical group-hover:translate-x-1 group-hover:opacity-100">
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </>
  )
}
